import React, { useEffect, useRef, useState } from 'react';
import { SubjectIdentity } from '../types';

interface SubjectMapProps {
  imageUrl: string;
  subjects: SubjectIdentity[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  /** When true, next click places a user-supplied face region. */
  placementMode?: boolean;
  onPlaceFace?: (nx: number, ny: number) => void;
  onCancelPlacement?: () => void;
  /** Person being located (for copy). */
  locatingPersonIndex?: number | null;
  /** Optional: show faint outlines for all mapped faces. Off by default. */
  showAllLocations?: boolean;
  onToggleShowAllLocations?: (next: boolean) => void;
}

export function hasUsableBounds(subj: SubjectIdentity): boolean {
  if (subj.faceBoundsSource === 'legacy') return false;
  const b = subj.faceBounds;
  if (!b) return false;
  const { xMin, yMin, xMax, yMax } = b;
  if (![xMin, yMin, xMax, yMax].every((n) => typeof n === 'number' && Number.isFinite(n))) {
    return false;
  }
  return xMax > xMin && yMax > yMin && xMin >= 0 && yMin >= 0 && xMax <= 1.05 && yMax <= 1.05;
}

/**
 * Clean photograph with selection highlight only.
 * Permanent numbered badges are not shown by default — Person IDs live on subject cards.
 * Internal Gemini annotated maps are separate and never shown here.
 */
export const SubjectMap: React.FC<SubjectMapProps> = ({
  imageUrl,
  subjects,
  selectedIndex,
  onSelect,
  placementMode = false,
  onPlaceFace,
  onCancelPlacement,
  locatingPersonIndex = null,
  showAllLocations = false,
  onToggleShowAllLocations,
}) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const withBounds = subjects.filter(hasUsableBounds);
  const selected = subjects.find((s) => s.index === selectedIndex);
  const selectedHasBounds = selected ? hasUsableBounds(selected) : false;

  if (withBounds.length === 0 && !placementMode && subjects.length === 0) return null;

  const handleStageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placementMode || !onPlaceFace || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    onPlaceFace(nx, ny);
  };

  return (
    <div className={`kin-subject-map${placementMode ? ' is-placing' : ''}`}>
      <div className="kin-subject-map-head">
        <div className="kin-subject-map-label">
          {placementMode
            ? locatingPersonIndex != null
              ? `Click Person #${locatingPersonIndex}'s face`
              : 'Click the person’s face'
            : 'People in this photo'}
        </div>
        {!placementMode && onToggleShowAllLocations && withBounds.length > 0 && (
          <label className="kin-subject-map-toggle">
            <input
              type="checkbox"
              checked={showAllLocations}
              onChange={(e) => onToggleShowAllLocations(e.target.checked)}
            />
            Show subject locations
          </label>
        )}
      </div>

      <div
        ref={stageRef}
        className="kin-subject-map-stage"
        onClick={handleStageClick}
        role={placementMode ? 'button' : undefined}
        aria-label={
          placementMode ? 'Click to locate a face on the photograph' : 'Photograph preview'
        }
      >
        <img
          src={imageUrl}
          alt="People in this photograph"
          className="kin-subject-map-img"
          draggable={false}
        />

        {showAllLocations &&
          !placementMode &&
          withBounds.map((subj) => {
            const b = subj.faceBounds!;
            return (
              <button
                key={`all-${subj.index}`}
                type="button"
                className="kin-subject-outline is-faint"
                style={{
                  left: `${Math.max(0, Math.min(1, b.xMin)) * 100}%`,
                  top: `${Math.max(0, Math.min(1, b.yMin)) * 100}%`,
                  width: `${Math.max(0, Math.min(1, b.xMax - b.xMin)) * 100}%`,
                  height: `${Math.max(0, Math.min(1, b.yMax - b.yMin)) * 100}%`,
                }}
                title={`Person #${subj.index}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onSelect(subj.index);
                }}
              >
                <span className="kin-subject-outline-tag">{subj.index}</span>
              </button>
            );
          })}

        {!placementMode && selectedHasBounds && selected?.faceBounds && (
          <div
            className="kin-subject-outline is-active"
            style={{
              left: `${Math.max(0, Math.min(1, selected.faceBounds.xMin)) * 100}%`,
              top: `${Math.max(0, Math.min(1, selected.faceBounds.yMin)) * 100}%`,
              width: `${Math.max(0, Math.min(1, selected.faceBounds.xMax - selected.faceBounds.xMin)) * 100}%`,
              height: `${Math.max(0, Math.min(1, selected.faceBounds.yMax - selected.faceBounds.yMin)) * 100}%`,
            }}
            aria-hidden
          >
            <span className="kin-subject-outline-tag">{selected.index}</span>
          </div>
        )}
      </div>

      <p className="kin-subject-map-hint">
        {placementMode ? (
          <>
            Crosshair mode: click approximately on the center of the correct face.{' '}
            {onCancelPlacement && (
              <button type="button" className="kin-subject-map-cancel" onClick={onCancelPlacement}>
                Cancel
              </button>
            )}
          </>
        ) : (
          <>Select a person below to highlight them in the photograph.</>
        )}
      </p>
    </div>
  );
};

/** Async face crop thumbnail for subject cards (UI-only). */
export const FaceThumbnail: React.FC<{
  imageUrl?: string;
  subject: SubjectIdentity;
  createThumb: (
    url: string,
    bounds: NonNullable<SubjectIdentity['faceBounds']>
  ) => Promise<string | null>;
}> = ({ imageUrl, subject, createThumb }) => {
  const [src, setSrc] = useState<string | null>(null);
  const usable = hasUsableBounds(subject);

  useEffect(() => {
    let cancelled = false;
    if (!usable || !imageUrl || !subject.faceBounds) {
      setSrc(null);
      return;
    }
    createThumb(imageUrl, subject.faceBounds).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [
    imageUrl,
    usable,
    subject.faceBounds?.xMin,
    subject.faceBounds?.yMin,
    subject.faceBounds?.xMax,
    subject.faceBounds?.yMax,
    createThumb,
  ]);

  if (!usable) {
    return (
      <div className="kin-face-thumb kin-face-thumb-empty" aria-hidden>
        ?
      </div>
    );
  }

  return src ? (
    <img src={src} alt="" className="kin-face-thumb" draggable={false} />
  ) : (
    <div className="kin-face-thumb kin-face-thumb-loading" aria-hidden />
  );
};
